import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, Users, ClipboardList, Receipt, AlertTriangle, TrendingUp, Calendar, ArrowUpRight, ArrowDownRight, Scale, Clock, ChevronDown, ChevronUp, Wallet, Eye, EyeOff, CalendarRange, BarChart3, FileDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { toast } from 'sonner';
import PromemoriaContiWidget from '@/components/dashboard/PromemoriaContiWidget';
import CalendarioNoteWidget from '@/components/dashboard/CalendarioNoteWidget';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (v) => (v || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const CREDITO_TOGGLE_KEY = 'dashboard_credito_totale_visible';
const CHARTS_TOGGLE_KEY = 'dashboard_charts_visible';
const PIE_COLORS = ['#10b981', '#3b82f6']; // emerald (Incassato), blue (Da Incassare)
// Palette contrastata per il line chart trend clienti (max 5 clienti)
const TREND_COLORS = ['#2563eb', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6'];

// Tooltip Euro formatter for recharts
const eurTooltip = (v) => `€ ${fmt(v)}`;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [scadenze, setScadenze] = useState([]);
  const [loading, setLoading] = useState(true);
  // iter103: widget nascosti impostati dal super-admin per questo admin
  const [hiddenWidgets, setHiddenWidgets] = useState([]);
  const showW = (k) => !hiddenWidgets.includes(k);
  // iter78: anno selezionato + anni disponibili + credito cumulativo
  const currentYear = new Date().getFullYear();
  const [annoSelezionato, setAnnoSelezionato] = useState(currentYear);
  const [anniDisponibili, setAnniDisponibili] = useState([currentYear]);
  const [anniOpen, setAnniOpen] = useState(false);
  const [credito, setCredito] = useState(null);
  const [creditoVisible, setCreditoVisible] = useState(() => {
    try { return localStorage.getItem(CREDITO_TOGGLE_KEY) === '1'; } catch { return false; }
  });

  // iter79: grafici + report annuale
  const [grafici, setGrafici] = useState(null);
  const [trendClienti, setTrendClienti] = useState(null);
  const [redditivita, setRedditivita] = useState(null);
  const [graficiVisible, setGraficiVisible] = useState(() => {
    try { return localStorage.getItem(CHARTS_TOGGLE_KEY) !== '0'; } catch { return true; }
  });
  const [reportBusy, setReportBusy] = useState(false);

  const fetchStats = useCallback(async (anno) => {
    try {
      const [statsRes, scadRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { params: { anno }, withCredentials: true }),
        axios.get(`${API}/scadenze-imminenti`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setScadenze(scadRes.data);
    } catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  }, []);

  // Caricamento iniziale + reload al cambio anno
  useEffect(() => { fetchStats(annoSelezionato); }, [annoSelezionato, fetchStats]);

  useEffect(() => {
    axios.get(`${API}/dashboard/my-dashboard-config`, { withCredentials: true })
      .then(r => setHiddenWidgets(r.data?.hidden || []))
      .catch(() => setHiddenWidgets([]));
  }, []);

  // Anni disponibili — una tantum
  useEffect(() => {
    axios.get(`${API}/dashboard/anni-disponibili`, { withCredentials: true })
      .then(r => {
        const anni = r.data?.anni || [currentYear];
        setAnniDisponibili(anni);
      })
      .catch(() => {});
  }, [currentYear]);

  // Credito cumulativo — caricato solo quando visibile
  useEffect(() => {
    if (!creditoVisible) return;
    axios.get(`${API}/dashboard/credito-aperto`, { withCredentials: true })
      .then(r => setCredito(r.data))
      .catch(() => setCredito(null));
  }, [creditoVisible]);

  // iter79: grafici — caricati per l'anno selezionato quando visibili
  useEffect(() => {
    if (!graficiVisible) return;
    axios.get(`${API}/dashboard/grafici`, { params: { anno: annoSelezionato }, withCredentials: true })
      .then(r => setGrafici(r.data))
      .catch(() => setGrafici(null));
  }, [graficiVisible, annoSelezionato]);

  // iter80: trend clienti (top 5, ultimi 3 anni) — indipendente dall'anno selezionato
  useEffect(() => {
    if (!graficiVisible) return;
    axios.get(`${API}/dashboard/trend-clienti`, { params: { anni: 3, top: 5 }, withCredentials: true })
      .then(r => setTrendClienti(r.data))
      .catch(() => setTrendClienti(null));
  }, [graficiVisible]);

  // iter82: redditività cantieri. iter110: vista TOTALE come predefinita (tutti gli
  // anni, inclusi i cantieri chiusi) con filtri opzionali per anno e per cliente.
  const [reddAnno, setReddAnno] = useState(0);       // 0 = tutti gli anni (totale)
  const [reddCliente, setReddCliente] = useState(''); // '' = tutti i clienti
  useEffect(() => {
    if (!graficiVisible) return;
    const params = { top: 10, anno: reddAnno || 0 };
    if (reddCliente) params.cliente = reddCliente;
    axios.get(`${API}/dashboard/redditivita-cantieri`, { params, withCredentials: true })
      .then(r => setRedditivita(r.data))
      .catch(() => setRedditivita(null));
  }, [graficiVisible, reddAnno, reddCliente]);

  const toggleCredito = () => {
    const next = !creditoVisible;
    setCreditoVisible(next);
    try { localStorage.setItem(CREDITO_TOGGLE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
  };

  const toggleGrafici = () => {
    const next = !graficiVisible;
    setGraficiVisible(next);
    try { localStorage.setItem(CHARTS_TOGGLE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
  };

  const scaricaReportAnnuale = async () => {
    if (reportBusy) return;
    setReportBusy(true);
    const t = toast.loading(`Generazione Report Annuale ${annoSelezionato}…`);
    try {
      const r = await axios.get(`${API}/dashboard/report-annuale`, {
        params: { anno: annoSelezionato },
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Report_Annuale_${annoSelezionato}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.dismiss(t);
      toast.success(`Report Annuale ${annoSelezionato} scaricato`);
    } catch (err) {
      toast.dismiss(t);
      const detail = err?.response?.status === 403
        ? 'Permesso negato — non hai il diritto Dashboard/Mostra Importi'
        : 'Errore generazione report';
      toast.error(detail);
    } finally {
      setReportBusy(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  const statCards = [
    { label: 'Cantieri Attivi', value: stats?.active_cantieri || 0, icon: Building2, color: 'text-blue-600 bg-blue-100' },
    { label: 'Operai Totali', value: stats?.total_workers || 0, icon: Users, color: 'text-emerald-600 bg-emerald-100' },
    { label: 'Rapportini in Attesa', value: stats?.pending_rapportini || 0, icon: ClipboardList, color: 'text-amber-600 bg-amber-100' },
  ];

  const margine = stats?.margine || 0;
  const importiRedatti = stats?._importi_redatti === true;
  const anniPassati = (anniDisponibili || []).filter(a => a !== annoSelezionato && a < currentYear).sort((a,b) => b - a);
  const isCurrentYear = annoSelezionato === currentYear;

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="admin-dashboard">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-xl lg:text-3xl font-bold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-xs lg:text-sm text-slate-500 mt-0.5">
            Panoramica anno solare <span className="font-semibold text-slate-700">{annoSelezionato}</span>
            {!isCurrentYear && <span className="ml-1.5 text-amber-600">(anno passato)</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!importiRedatti && (
            <Button
              variant="outline"
              size="sm"
              onClick={scaricaReportAnnuale}
              disabled={reportBusy}
              data-testid="scarica-report-annuale"
              title={`Scarica il Report Annuale ${annoSelezionato} in PDF (con confronto anno precedente)`}
            >
              <FileDown className="h-4 w-4 mr-1.5" />
              {reportBusy ? 'Generazione…' : 'Report annuale PDF'}
            </Button>
          )}
          {!importiRedatti && (
            <Button
              variant={graficiVisible ? 'default' : 'outline'}
              size="sm"
              onClick={toggleGrafici}
              className={graficiVisible ? 'bg-slate-900 hover:bg-slate-800 text-white' : ''}
              data-testid="toggle-grafici"
              title="Mostra/nascondi i grafici mensili e la ripartizione Incassato vs Da Incassare"
            >
              <BarChart3 className="h-4 w-4 mr-1.5" />
              Grafici
            </Button>
          )}
          {!importiRedatti && (
            <Button
              variant={creditoVisible ? 'default' : 'outline'}
              size="sm"
              onClick={toggleCredito}
              className={creditoVisible ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : ''}
              data-testid="toggle-credito-totale"
              title="Mostra/nascondi il credito totale aperto cumulativo su tutti gli anni"
            >
              {creditoVisible ? <EyeOff className="h-4 w-4 mr-1.5" /> : <Eye className="h-4 w-4 mr-1.5" />}
              Credito totale
            </Button>
          )}
          <div className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1" data-testid="year-selector">
            <CalendarRange className="h-4 w-4 text-slate-500" />
            <Select value={String(annoSelezionato)} onValueChange={(v) => setAnnoSelezionato(parseInt(v, 10))}>
              <SelectTrigger className="h-8 w-[120px] border-0 bg-transparent px-1 focus:ring-0" data-testid="year-selector-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(anniDisponibili || []).map(a => (
                  <SelectItem key={a} value={String(a)} data-testid={`year-option-${a}`}>
                    {a}{a === currentYear ? ' (corrente)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!isCurrentYear && (
              <button
                type="button"
                onClick={() => setAnnoSelezionato(currentYear)}
                className="ml-1 text-[11px] text-blue-600 hover:text-blue-800 underline whitespace-nowrap"
                data-testid="reset-year-btn"
              >
                Torna a {currentYear}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* iter78: Casella "Credito totale aperto" cumulativa su tutti gli anni */}
      {showW('credito_totale') && creditoVisible && !importiRedatti && (
        <Card className="border-indigo-300 bg-gradient-to-r from-indigo-50 to-blue-50" data-testid="credito-totale-card">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-indigo-100 rounded-lg">
                  <Wallet className="h-5 w-5 text-indigo-700" />
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-indigo-700 font-semibold">Credito totale aperto — cumulativo su tutti gli anni</p>
                  <p className="text-2xl lg:text-3xl font-bold text-indigo-900" data-testid="credito-totale-value">
                    {credito ? fmt(credito.totale) : '…'} <span className="text-base font-normal">€</span>
                  </p>
                  <p className="text-[11px] text-indigo-600 mt-0.5">
                    {credito?.count_fatture > 0 && <span>{credito.count_fatture} {credito.count_fatture === 1 ? 'fattura non incassata' : 'fatture non incassate'}</span>}
                    {credito?.count_fatture > 0 && credito?.count_conti > 0 && <span> · </span>}
                    {credito?.count_conti > 0 && <span>{credito.count_conti} {credito.count_conti === 1 ? 'conto emesso da fatturare' : 'conti emessi da fatturare'}</span>}
                    {(!credito?.count_fatture && !credito?.count_conti) && <span>Nessun credito aperto</span>}
                  </p>
                </div>
              </div>
              {credito?.per_anno?.length > 0 && (
                <div className="text-xs text-indigo-800 min-w-[260px]">
                  <p className="font-semibold mb-1">Distribuzione per anno:</p>
                  <div className="space-y-0.5 max-h-32 overflow-y-auto">
                    {credito.per_anno.map(r => (
                      <div key={r.anno} className="flex justify-between gap-3" data-testid={`credito-anno-${r.anno}`}>
                        <span>
                          {r.anno || 'non specificato'}
                          {(r.da_fatture > 0 || r.da_conti > 0) && (
                            <span className="text-indigo-500 ml-1">
                              ({r.da_fatture > 0 && <span title="Fatture non incassate">F: {fmt(r.da_fatture)}</span>}
                              {r.da_fatture > 0 && r.da_conti > 0 && ' · '}
                              {r.da_conti > 0 && <span title="Conti emessi non fatturati">C: {fmt(r.da_conti)}</span>})
                            </span>
                          )}
                        </span>
                        <span className="font-mono font-semibold whitespace-nowrap">{fmt(r.totale)} €</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      {showW('stats') && (
      <div className="grid grid-cols-3 gap-2 lg:gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.label} className="border-slate-200">
              <CardContent className="p-3 lg:p-5">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[11px] lg:text-sm text-slate-500 leading-tight">{stat.label}</p>
                    <p className="text-xl lg:text-2xl font-bold text-slate-900 mt-0.5 lg:mt-1">{stat.value}</p>
                  </div>
                  <div className={`p-2 lg:p-3 rounded-lg shrink-0 ${stat.color}`}><Icon className="h-4 w-4 lg:h-5 lg:w-5" /></div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      )}

      {/* iter61: Banner informativo quando importi sono redatti */}
      {importiRedatti && (
        <div className="bg-slate-100 border border-slate-300 rounded-md p-3 flex items-center gap-2 text-xs text-slate-600" data-testid="importi-redacted-banner">
          <span className="text-lg">🔒</span>
          <span>Gli importi finanziari sono nascosti perché non hai il permesso <strong>&quot;Dashboard - Mostra Importi&quot;</strong>.</span>
        </div>
      )}

      {/* Riepilogo Finanziario Principale */}
      {showW('riepilogo_finanziario') && !importiRedatti && (
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-2 lg:gap-4">
        <Card className="border-purple-200 bg-purple-50/50">
          <CardContent className="p-3 lg:p-5">
            <div className="flex items-center gap-1.5 lg:gap-2 mb-1">
              <Receipt className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-purple-600" />
              <p className="text-[10px] lg:text-xs text-purple-600 uppercase font-medium">Fatturato Emesso</p>
            </div>
            <p className="text-lg lg:text-2xl font-bold text-purple-700 break-all" data-testid="fatturato-emesso">{fmt(stats?.fatturato_totale)}</p>
            <p className="text-[10px] text-purple-500 mt-0.5">anno {annoSelezionato}</p>
            {stats?.note_credito_uscita > 0 && (
              <p className="text-[10px] text-purple-500 mt-0.5" title="Fatturato netto dopo le note di credito (art.26 DPR 633/72)">
                Netto: <span className="font-semibold">{fmt(stats?.fatturato_netto)}</span>
              </p>
            )}
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-3 lg:p-5">
            <div className="flex items-center gap-1.5 lg:gap-2 mb-1">
              <ArrowUpRight className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-emerald-600" />
              <p className="text-[10px] lg:text-xs text-emerald-600 uppercase font-medium">Pagamenti Ricevuti</p>
            </div>
            <p className="text-lg lg:text-2xl font-bold text-emerald-700 break-all" data-testid="fatturato-incassato">{fmt(stats?.fatturato_incassato)}</p>
            <p className="text-[10px] text-emerald-500 mt-0.5">effettivamente incassato · anno {annoSelezionato}</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-3 lg:p-5">
            <div className="flex items-center gap-1.5 lg:gap-2 mb-1">
              <Clock className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-blue-600" />
              <p className="text-[10px] lg:text-xs text-blue-600 uppercase font-medium">Da Incassare</p>
            </div>
            <p className="text-lg lg:text-2xl font-bold text-blue-700 break-all" data-testid="da-incassare">{fmt(stats?.fatturato_da_incassare)}</p>
            <p className="text-[10px] text-blue-500 mt-0.5">anno {annoSelezionato}</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="p-3 lg:p-5">
            <div className="flex items-center gap-1.5 lg:gap-2 mb-1">
              <ArrowDownRight className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-red-600" />
              <p className="text-[10px] lg:text-xs text-red-600 uppercase font-medium">Spese da Pagare</p>
            </div>
            <p className="text-lg lg:text-2xl font-bold text-red-700 break-all" data-testid="spese-da-pagare">{fmt(stats?.spese_da_pagare)}</p>
            <p className="text-[10px] text-red-500 mt-0.5">anno {annoSelezionato}</p>
          </CardContent>
        </Card>
        <Card className={`col-span-2 lg:col-span-1 ${margine >= 0 ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
          <CardContent className="p-3 lg:p-5">
            <div className="flex items-center gap-1.5 lg:gap-2 mb-1">
              <Scale className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-slate-600" />
              <p className="text-[10px] lg:text-xs text-slate-600 uppercase font-medium">Margine</p>
            </div>
            <p className={`text-lg lg:text-2xl font-bold break-all ${margine >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="margine">{fmt(margine)}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">anno {annoSelezionato}</p>
          </CardContent>
        </Card>
      </div>
      )}

      {/* iter86-g: Card "Fatture Ricevute" (spese pagate + accertate) */}
      {showW('fatture_ricevute') && !importiRedatti && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 lg:gap-4">
        <Card className="border-orange-200 bg-orange-50/50">
          <CardContent className="p-3 lg:p-5 flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 lg:gap-2 mb-1">
                <ArrowDownRight className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-orange-600" />
                <p className="text-[10px] lg:text-xs text-orange-700 uppercase font-medium">Fatture Ricevute</p>
              </div>
              <p className="text-lg lg:text-2xl font-bold text-orange-800 break-all" data-testid="fatture-ricevute-pagate">{fmt(stats?.spese_pagate)}</p>
              <p className="text-[10px] text-orange-600 mt-0.5">effettivamente pagate · anno {annoSelezionato}</p>
              <p className="text-[10px] text-slate-600 mt-1" title="Totale fatture ricevute nell'anno indipendentemente dallo stato di pagamento — costi accertati">
                Totale accertato: <span className="font-semibold text-slate-800" data-testid="fatture-ricevute-totale">{fmt(stats?.spese_totali)}</span>
              </p>
            </div>
            <a href="/admin/fatture-entrata" className="text-xs text-orange-700 underline hover:text-orange-900 whitespace-nowrap" data-testid="link-fatture-ricevute">Vedi tutte →</a>
          </CardContent>
        </Card>
      </div>
      )}

      {/* iter51: Conti Emessi + Fatturato non Esigibile (secondary KPIs) */}
      {showW('conti_emessi_kpi') && !importiRedatti && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 lg:gap-4">
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-3 lg:p-5 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-1.5 lg:gap-2 mb-1">
                <Receipt className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-amber-600" />
                <p className="text-[10px] lg:text-xs text-amber-700 uppercase font-medium">Conti Emessi da Fatturare — per anno</p>
              </div>
              {(stats?.conti_da_fatturare_per_anno || []).length === 0 ? (
                <>
                  <p className="text-lg lg:text-2xl font-bold text-amber-700 break-all" data-testid="conti-da-fatturare">{fmt(stats?.conti_da_fatturare)}</p>
                  <p className="text-[10px] text-amber-600 mt-0.5">Nessun conto da fatturare</p>
                </>
              ) : (
                <div className="space-y-0.5 mt-1" data-testid="conti-da-fatturare-per-anno">
                  {(stats?.conti_da_fatturare_per_anno || []).map((r) => (
                    <div key={r.anno} className="flex items-center justify-between gap-3" data-testid={`conti-da-fatturare-anno-${r.anno}`}>
                      <span className="text-[11px] lg:text-xs text-amber-700">{r.anno || 'n/d'} <span className="text-amber-500">({r.count})</span></span>
                      <span className="text-sm lg:text-base font-bold text-amber-800 font-mono break-all">{fmt(r.importo)} €</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between gap-3 border-t border-amber-200 pt-0.5 mt-0.5">
                    <span className="text-[10px] uppercase text-amber-600 font-semibold">Totale</span>
                    <span className="text-sm lg:text-base font-bold text-amber-900 font-mono break-all" data-testid="conti-da-fatturare">
                      {fmt((stats?.conti_da_fatturare_per_anno || []).reduce((s, r) => s + (r.importo || 0), 0))} €
                    </span>
                  </div>
                </div>
              )}
            </div>
            <a href="/admin/conti-emessi" className="text-xs text-amber-700 underline hover:text-amber-900 whitespace-nowrap" data-testid="link-conti-emessi">Vedi tutti →</a>
          </CardContent>
        </Card>
        <Card className="border-slate-200 bg-slate-50/50">
          <CardContent className="p-3 lg:p-5">
            <div className="flex items-center gap-1.5 lg:gap-2 mb-1">
              <AlertTriangle className="h-3.5 w-3.5 lg:h-4 lg:w-4 text-slate-500" />
              <p className="text-[10px] lg:text-xs text-slate-600 uppercase font-medium">Fatturato non Esigibile (stornato)</p>
            </div>
            <p className="text-lg lg:text-2xl font-bold text-slate-700 break-all" data-testid="fatturato-non-esigibile">{fmt(stats?.fatturato_non_esigibile)}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">Fatture stornate — restano nel registro IVA ma non sono da incassare · anno {annoSelezionato}</p>
          </CardContent>
        </Card>
      </div>
      )}

      {/* iter101/iter103: Promemoria Conti + Agenda (visibilità gestita dal super-admin) */}
      {(showW('promemoria_conti') || showW('agenda')) && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 lg:gap-4" data-testid="dashboard-widgets-row">
        {showW('promemoria_conti') && <PromemoriaContiWidget />}
        {showW('agenda') && <CalendarioNoteWidget />}
      </div>
      )}

      {/* iter79: Grafici — Bar chart mensile + Pie chart Incassato vs Da Incassare */}
      {showW('grafici') && !importiRedatti && graficiVisible && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="dashboard-grafici">
          <Card className="border-slate-200 lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-slate-500" />
                Andamento mensile — {annoSelezionato}
              </CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">Fatturato · Incassato · Spese per ciascun mese</p>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="h-72" data-testid="bar-chart-mensile">
                {grafici?.per_mese ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={grafici.per_mese} margin={{ top: 8, right: 8, bottom: 4, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#475569' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#475569' }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                      <Tooltip formatter={eurTooltip} contentStyle={{ fontSize: '12px' }} />
                      <Legend wrapperStyle={{ fontSize: '11px' }} />
                      <Bar dataKey="fatturato" name="Fatturato" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="incassato" name="Incassato" fill="#10b981" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="spese" name="Spese" fill="#ef4444" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full text-sm text-slate-400">Caricamento…</div>
                )}
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Receipt className="h-4 w-4 text-slate-500" />
                Incassato vs Da Incassare
              </CardTitle>
              <p className="text-xs text-slate-500 mt-0.5">Anno {annoSelezionato}</p>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="h-72 flex items-center justify-center" data-testid="pie-chart-incassi">
                {grafici?.pie ? (
                  (grafici.pie[0].value + grafici.pie[1].value) > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={grafici.pie}
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={90}
                          paddingAngle={2}
                          dataKey="value"
                          label={(entry) => `${entry.name}: ${((entry.value / (grafici.pie[0].value + grafici.pie[1].value)) * 100).toFixed(0)}%`}
                        >
                          {grafici.pie.map((_, i) => (
                            <Cell key={`cell-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={eurTooltip} contentStyle={{ fontSize: '12px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="text-center">
                      <p className="text-sm text-slate-400">Nessuna fattura per l&apos;anno {annoSelezionato}</p>
                    </div>
                  )
                ) : (
                  <div className="text-sm text-slate-400">Caricamento…</div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* iter80: Trend Clienti — Top 5 clienti negli ultimi 3 anni */}
      {showW('trend_clienti') && !importiRedatti && graficiVisible && trendClienti && trendClienti.clienti && trendClienti.clienti.length > 0 && (
        <Card className="border-slate-200" data-testid="trend-clienti-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-slate-500" />
              Trend clienti — Top {trendClienti.clienti.length} negli ultimi {trendClienti.anni.length} anni
            </CardTitle>
            <p className="text-xs text-slate-500 mt-0.5">
              Fatturato per anno solare dei clienti con maggiore volume cumulativo · {trendClienti.anni[0]}–{trendClienti.anni[trendClienti.anni.length - 1]}
            </p>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              <div className="lg:col-span-3 h-72" data-testid="line-chart-trend-clienti">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendClienti.chart_data} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="anno" tick={{ fontSize: 11, fill: '#475569' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#475569' }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                    <Tooltip formatter={eurTooltip} contentStyle={{ fontSize: '12px' }} />
                    <Legend wrapperStyle={{ fontSize: '11px' }} />
                    {trendClienti.clienti.map((c, i) => (
                      <Line
                        key={c.cliente}
                        type="monotone"
                        dataKey={c.cliente}
                        stroke={TREND_COLORS[i % TREND_COLORS.length]}
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {/* Ranking laterale */}
              <div className="space-y-2" data-testid="trend-clienti-ranking">
                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Ranking totale</p>
                {trendClienti.clienti.map((c, i) => (
                  <div
                    key={c.cliente}
                    className="flex items-center gap-2 p-2 rounded-md bg-slate-50 border border-slate-200"
                    data-testid={`trend-cliente-row-${i}`}
                  >
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: TREND_COLORS[i % TREND_COLORS.length] }} />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-slate-900 truncate" title={c.cliente}>{c.cliente}</p>
                      <p className="text-[10px] text-slate-500 font-mono">€ {fmt(c.totale)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* iter82/iter86-j: Analisi Redditività Cantieri — Top vs Bottom margine, con toggle Totale */}
      {showW('redditivita') && !importiRedatti && graficiVisible && redditivita && redditivita.top && (
        <Card className="border-slate-200" data-testid="redditivita-card">
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Scale className="h-4 w-4 text-slate-500" />
                  Redditività cantieri — {reddAnno ? <span>anno {reddAnno}</span> : <span className="text-indigo-700">Totale (tutti gli anni)</span>}
                  {reddCliente && <span className="text-slate-600"> · {reddCliente}</span>}
                </CardTitle>
                <p className="text-xs text-slate-500 mt-0.5">
                  Ricavo (imponibile fatturato) vs Costo (materiali + manodopera) per i cantieri con margine più alto e più basso.
                  <span className="ml-2 text-slate-700">
                    {redditivita.totale_cantieri} cantieri (inclusi i chiusi) ·
                    margine complessivo € <span className={redditivita.totale_margine >= 0 ? 'text-emerald-700 font-semibold' : 'text-red-700 font-semibold'}>{fmt(redditivita.totale_margine)}</span>
                  </span>
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap" data-testid="redditivita-filtri">
                <Select value={String(reddAnno)} onValueChange={(v) => setReddAnno(Number(v))}>
                  <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="redditivita-anno-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Tutti gli anni</SelectItem>
                    {[...new Set([...(reddAnno ? [reddAnno] : []), ...(redditivita.anni || [])])].sort((a, b) => b - a).map((a) => (
                      <SelectItem key={a} value={String(a)}>Anno {a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={reddCliente || 'all'} onValueChange={(v) => setReddCliente(v === 'all' ? '' : v)}>
                  <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="redditivita-cliente-select">
                    <SelectValue placeholder="Tutti i clienti" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti i clienti</SelectItem>
                    {[...new Set([...(redditivita.clienti || []), ...(reddCliente ? [reddCliente] : [])])].map((cl) => (
                      <SelectItem key={cl} value={cl}>{cl}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            {(redditivita.top.length + (redditivita.bottom ? redditivita.bottom.length : 0)) > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* TOP 10 — cantieri più redditizi */}
              <div data-testid="redditivita-top">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <p className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">
                    Top {redditivita.top.length} — margine più alto
                  </p>
                </div>
                <div style={{ height: `${Math.max(220, redditivita.top.length * 34)}px` }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={redditivita.top} layout="vertical" margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: '#475569' }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                      <YAxis
                        type="category"
                        dataKey="cantiere_name"
                        tick={{ fontSize: 10, fill: '#475569' }}
                        width={130}
                        tickFormatter={(v) => v && v.length > 22 ? v.slice(0, 22) + '…' : v}
                      />
                      <Tooltip
                        formatter={eurTooltip}
                        labelFormatter={(l, payload) => {
                          const p = payload?.[0]?.payload;
                          return p ? `${p.cantiere_name}${p.client_name ? ` · ${p.client_name}` : ''} — margine ${fmt(p.margine)} € (${p.margine_pct}%)` : l;
                        }}
                        contentStyle={{ fontSize: '11px' }}
                      />
                      <Legend wrapperStyle={{ fontSize: '10px' }} />
                      <Bar dataKey="ricavo" name="Ricavo" fill="#10b981" radius={[0, 3, 3, 0]} />
                      <Bar dataKey="costo" name="Costo" fill="#ef4444" radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* BOTTOM 10 — cantieri meno redditizi (o in perdita) */}
              {redditivita.bottom && redditivita.bottom.length > 0 && (
                <div data-testid="redditivita-bottom">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    <p className="text-xs uppercase tracking-wider text-red-700 font-semibold">
                      Bottom {redditivita.bottom.length} — margine più basso
                    </p>
                  </div>
                  <div style={{ height: `${Math.max(220, redditivita.bottom.length * 34)}px` }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={redditivita.bottom} layout="vertical" margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 10, fill: '#475569' }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                        <YAxis
                          type="category"
                          dataKey="cantiere_name"
                          tick={{ fontSize: 10, fill: '#475569' }}
                          width={130}
                          tickFormatter={(v) => v && v.length > 22 ? v.slice(0, 22) + '…' : v}
                        />
                        <Tooltip
                          formatter={eurTooltip}
                          labelFormatter={(l, payload) => {
                            const p = payload?.[0]?.payload;
                            return p ? `${p.cantiere_name}${p.client_name ? ` · ${p.client_name}` : ''} — margine ${fmt(p.margine)} € (${p.margine_pct}%)` : l;
                          }}
                          contentStyle={{ fontSize: '11px' }}
                        />
                        <Legend wrapperStyle={{ fontSize: '10px' }} />
                        <Bar dataKey="ricavo" name="Ricavo" fill="#10b981" radius={[0, 3, 3, 0]} />
                        <Bar dataKey="costo" name="Costo" fill="#ef4444" radius={[0, 3, 3, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
            ) : (
              <p className="text-sm text-slate-500 py-8 text-center" data-testid="redditivita-empty">Nessun cantiere con attività per i filtri selezionati.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dettaglio Ricavi e Spese + Scadenze */}
      {showW('entrate_uscite') && !importiRedatti && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Riepilogo Entrate/Uscite */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-slate-500" />Riepilogo Ricavi e Spese <span className="text-xs font-normal text-slate-500 ml-1">({annoSelezionato})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
              <div className="flex justify-between items-center">
                <span className="text-sm text-emerald-700 font-medium">Fatturato Totale (uscita)</span>
                <span className="text-lg font-bold text-emerald-800">{fmt(stats?.fatturato_totale)}</span>
              </div>
              <div className="flex justify-between items-center mt-1 text-xs text-emerald-600">
                <span>Incassato: {fmt(stats?.fatturato_incassato)}</span>
                <span>Da incassare: {fmt(stats?.fatturato_da_incassare)}</span>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-red-50 border border-red-200">
              <div className="flex justify-between items-center">
                <span className="text-sm text-red-700 font-medium">Spese Totali (entrata)</span>
                <span className="text-lg font-bold text-red-800">{fmt(stats?.spese_totali)}</span>
              </div>
              <div className="flex justify-between items-center mt-1 text-xs text-red-600">
                <span>Pagate: {fmt(stats?.spese_pagate)}</span>
                <span>Da pagare: {fmt(stats?.spese_da_pagare)}</span>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-700 font-medium">Margine Netto</span>
                <span className={`text-lg font-bold ${margine >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(margine)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Scadenze */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Calendar className="h-5 w-5 text-slate-500" />Scadenze Fatture (Entrata)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {scadenze.length > 0 ? (
              <div className="space-y-3">
                {scadenze.slice(0, 8).map((f) => {
                  const isOverdue = f.scadenza < new Date().toISOString().split('T')[0];
                  return (
                    <div key={f.id || `${f.fornitore}-${f.numero}-${f.scadenza}`} className={`flex items-center justify-between p-3 rounded-lg border ${isOverdue ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{f.fornitore} - {f.numero}</p>
                        <p className={`text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-amber-600'}`}>
                          {isOverdue ? 'SCADUTA' : 'Scade il'} {f.scadenza}
                        </p>
                      </div>
                      <Badge variant="outline" className={isOverdue ? 'text-red-700 border-red-300 bg-red-100' : 'text-amber-700 border-amber-300 bg-amber-100'}>
                        {fmt(f.importo)}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-500 text-sm py-4 text-center">Nessuna scadenza imminente</p>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* iter78: Sezione "Anni precedenti di fatturazione" collassata di default */}
      {showW('anni_precedenti') && anniPassati.length > 0 && (
        <Card className="border-slate-200" data-testid="anni-precedenti-section">
          <CardHeader className="pb-3 cursor-pointer select-none" onClick={() => setAnniOpen(!anniOpen)} data-testid="anni-precedenti-toggle">
            <CardTitle className="text-base font-semibold flex items-center justify-between">
              <span className="flex items-center gap-2">
                <CalendarRange className="h-4 w-4 text-slate-500" />
                Anni precedenti di fatturazione
                <span className="text-xs font-normal text-slate-500">({anniPassati.length} {anniPassati.length === 1 ? 'anno disponibile' : 'anni disponibili'})</span>
              </span>
              {anniOpen ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
            </CardTitle>
          </CardHeader>
          {anniOpen && (
            <CardContent>
              <p className="text-xs text-slate-500 mb-3">Clicca un anno per visualizzare la dashboard di quell&apos;anno. Il credito totale aperto resta cumulativo su tutti gli anni.</p>
              <div className="flex flex-wrap gap-2">
                {anniPassati.map(a => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => { setAnnoSelezionato(a); setAnniOpen(false); window.scrollTo({top: 0, behavior: 'smooth'}); }}
                    className="px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 text-sm font-medium transition-colors"
                    data-testid={`anno-passato-${a}`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}
